/**
 * CHDS first-party analytics tracker.
 *
 * Sends compact beacons to /api/collect/ on every storefront page view and on
 * key commerce events (product view, add to cart, checkout, purchase). Identity
 * is a first-party cookie (chds_vid, 2 years) so returning visitors and
 * multi-session funnels are accurate. No third party, no consent banner — the
 * disclosure lives in the privacy policy.
 *
 * The staff dashboard (/dashboard/*) is never tracked.
 */
import { firePageView as pixelPageView, fireEvent as pixelEvent, metaCookies } from "./pixels";

/** What checkout sends the server so the Conversions API can match the later
 *  server-side Purchase to this browser / ad click. */
export function checkoutTracking() {
  try { return { ...metaCookies(), url: window.location.href.slice(0, 512) }; } catch (e) { return {}; }
}

const API = process.env.VUE_APP_API_URL || "/api";
const COLLECT_URL = `${API.replace(/\/$/, "")}/collect/`;

const VID_DAYS = 730; // 2 years
const SID_MINUTES = 30;

let lastPageTs = 0; // for time-on-page
let heartbeatTimer = null;
const landingRef = typeof document !== "undefined" ? document.referrer : "";
const landingQuery = typeof window !== "undefined" ? window.location.search : "";

function uuid() {
  try {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  } catch (e) { /* noop */ }
  return "xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function cookieDomain() {
  try {
    const h = window.location.hostname || "";
    if (h.endsWith("chds.com.au")) return "; domain=.chds.com.au";
  } catch (e) { /* noop */ }
  return "";
}

function getCookie(name) {
  const m = document.cookie.match("(^|;)\\s*" + name + "\\s*=\\s*([^;]+)");
  return m ? decodeURIComponent(m.pop()) : "";
}

function setCookie(name, value, minutes) {
  const d = new Date();
  d.setTime(d.getTime() + minutes * 60 * 1000);
  document.cookie =
    `${name}=${encodeURIComponent(value)}; expires=${d.toUTCString()}; path=/; SameSite=Lax${cookieDomain()}` +
    (window.location.protocol === "https:" ? "; Secure" : "");
}

function getVid() {
  let v = getCookie("chds_vid");
  if (!v) { v = uuid(); }
  setCookie("chds_vid", v, VID_DAYS * 24 * 60); // refresh expiry
  return v;
}

function getSid() {
  let s = getCookie("chds_sid");
  if (!s) { s = uuid(); }
  setCookie("chds_sid", s, SID_MINUTES); // sliding 30-min window
  return s;
}

function isTrackablePath(path) {
  return !!path && !path.startsWith("/dashboard");
}

// The logged-in customer's id (if any) — lets the dashboard reconstruct each
// customer's on-site journey. Read from the app's stored user; never throws.
function getUid() {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "null");
    return u && u.id ? u.id : undefined;
  } catch (e) { return undefined; }
}

function send(kind, extra = {}) {
  try {
    const path = window.location.pathname;
    if (!isTrackablePath(path)) return;
    const now = Date.now();
    const tp = lastPageTs ? Math.round((now - lastPageTs) / 1000) : 0;
    const payload = {
      vid: getVid(),
      sid: getSid(),
      uid: getUid(),
      kind,
      path,
      title: document.title || "",
      ref: landingRef,
      q: landingQuery,
      tp: kind === "pageview" ? 0 : tp, // tp meaningful for the page you're leaving
      w: window.screen ? window.screen.width : undefined,
      h: window.screen ? window.screen.height : undefined,
      lang: navigator.language || "",
      ...extra,
    };
    fetch(COLLECT_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
      mode: "cors",
      credentials: "omit",
    }).catch(() => {});
  } catch (e) { /* analytics must never throw */ }
}

/** Record a page view (call on every storefront route change). */
export function trackPageview() {
  send("pageview");
  lastPageTs = Date.now();
  resetHeatmapPage();
  try { pixelPageView(); } catch (e) { /* noop */ }
}

// Kinds the first-party collector stores natively; anything else lands as "custom"
// with meta.name = the kind, so the Traffic dashboard can still tell them apart.
const NATIVE_KINDS = new Set(["product_view", "add_to_cart", "begin_checkout", "purchase"]);

/** Generic commerce/custom event. opts also carries items / eventId / numItems for the
 *  marketing pixels (see analytics/pixels.js). */
export function trackEvent(kind, opts = {}) {
  const meta = { ...(opts.meta || {}) };
  if (!NATIVE_KINDS.has(kind)) meta.name = meta.name || kind;
  if (opts.numItems != null) meta.num_items = opts.numItems;
  send(NATIVE_KINDS.has(kind) ? kind : "custom", {
    value: opts.value,
    order_id: opts.orderId,
    meta,
  });
  try { pixelEvent(kind, opts); } catch (e) { /* noop */ }
}

export const trackProductView = (opts = {}) => trackEvent("product_view", opts);
export const trackAddToCart = (opts = {}) => trackEvent("add_to_cart", opts);
export const trackBeginCheckout = (opts = {}) => trackEvent("begin_checkout", opts);
export const trackAddPaymentInfo = (opts = {}) => trackEvent("add_payment_info", opts);
export const trackPurchase = (opts = {}) => trackEvent("purchase", opts);
export const trackCompleteRegistration = (opts = {}) => trackEvent("complete_registration", opts);
export const trackSearch = (opts = {}) => trackEvent("search", opts);

/** Purchase events must fire once per order even if the confirmation page is reloaded.
 *  Remembers fired order ids in localStorage (bounded); returns true when NEW. */
export function markPurchaseOnce(orderId) {
  try {
    const key = "chds_px_purchased";
    const seen = JSON.parse(localStorage.getItem(key) || "[]");
    if (seen.includes(String(orderId))) return false;
    seen.push(String(orderId));
    localStorage.setItem(key, JSON.stringify(seen.slice(-50)));
    return true;
  } catch (e) { return true; }
}

/** Content id shared with the server-side Conversions API (apps/analytics/meta_capi.py):
 *  SKU when set, else variant-<portion id>; packages pkg-<code>; cookware "cookware". */
export function contentId(line = {}) {
  if (line.kind === "package") return `pkg-${line.code || line.package_code || "package"}`;
  if (line.kind === "cookware") return "cookware";
  const sku = String(line.sku || "").trim();
  if (sku) return sku;
  const vid = line.variant_id || line.selected_meal_portion_id || line.portion_price_id;
  return vid ? `variant-${vid}` : `item-${line.meal_id || line.id || "unknown"}`;
}

// --- Heatmap groundwork: sample clicks + scroll depth (privacy-safe: coordinates
// and element tag only, never content). Bounded per page to keep event volume sane. ---
let clickCount = 0;
let scrollMarks = {};
const CLICK_CAP_PER_PAGE = 12;
let scrollTick = false;

function resetHeatmapPage() { clickCount = 0; scrollMarks = {}; }

function onHeatClick(e) {
  try {
    if (clickCount >= CLICK_CAP_PER_PAGE) return;
    clickCount += 1;
    const vw = window.innerWidth || 1;
    const vh = window.innerHeight || 1;
    const fullH = Math.max(document.documentElement.scrollHeight || vh, vh);
    const el = e.target && e.target.tagName ? e.target.tagName.toLowerCase() : "";
    send("custom", { meta: {
      name: "click",
      xp: Math.round(((e.clientX || 0) / vw) * 1000) / 10,                       // % of viewport width
      yp: Math.round((((e.clientY || 0) + (window.scrollY || 0)) / fullH) * 1000) / 10, // % of full-page height
      vw, vh, el,
    }});
  } catch (e2) { /* analytics must never throw */ }
}

function onHeatScroll() {
  if (scrollTick) return;
  scrollTick = true;
  requestAnimationFrame(() => {
    scrollTick = false;
    try {
      const st = window.scrollY || document.documentElement.scrollTop || 0;
      const doch = Math.max((document.documentElement.scrollHeight || 0) - window.innerHeight, 1);
      const pct = Math.min(100, Math.round((st / doch) * 100));
      [25, 50, 75, 100].forEach((m) => {
        if (pct >= m && !scrollMarks[m]) {
          scrollMarks[m] = true;
          send("custom", { meta: { name: "scroll_depth", depth: m } });
        }
      });
    } catch (e2) { /* noop */ }
  });
}

function startHeartbeat() {
  if (heartbeatTimer) return;
  heartbeatTimer = setInterval(() => {
    if (document.visibilityState === "visible") send("heartbeat");
  }, 20000);
}

/** Initialise tracking. Call once after the app mounts. */
export function initAnalytics() {
  if (typeof window === "undefined") return;
  lastPageTs = Date.now();
  startHeartbeat();
  // capture the final dwell time when the tab is hidden/closed
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") send("heartbeat");
  });
  window.addEventListener("pagehide", () => send("heartbeat"));
  // Heatmap groundwork — capture where people click + how far they scroll.
  document.addEventListener("click", onHeatClick, { passive: true, capture: true });
  window.addEventListener("scroll", onHeatScroll, { passive: true });
}
